import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Pagination,
  Chip,
  Skeleton,
  Stack,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Comment as CommentIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import TimezoneUtils from '../utils/timezoneUtils';
import { announcementService } from '../services/announcementService';
import CreateAnnouncementDialog from '../components/CreateAnnouncementDialog';
import AnnouncementDetailDialog from '../components/AnnouncementDetailDialog';

const formatDate = (date) => {
  if (!date) return '';
  try {
    return TimezoneUtils.formatDateForDisplay(new Date(date), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

const formatAudience = (audience) => {
  if (!audience || audience.all) {
    return 'All users';
  }

  const parts = [];
  if (audience.roles?.length) {
    parts.push(audience.roles.join(', '));
  }
  if (audience.teamIds?.length) {
    parts.push(audience.teamIds.map((team) => team.name || 'Team').join(', '));
  }
  return parts.join(' · ') || 'All users';
};

const AnnouncementList = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isBoss = user?.role === 'boss';

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const data = await announcementService.getAnnouncements(page, 20);
      setAnnouncements(data.announcements || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      toast.error('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    const openAnnouncementId = location.state?.openAnnouncementId;
    if (openAnnouncementId) {
      setSelectedAnnouncementId(openAnnouncementId);
      setDetailDialogOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.openAnnouncementId]);

  useEffect(() => {
    if (isBoss) {
      axios.get(`${process.env.REACT_APP_API_URL}/teams/all`)
        .then((response) => setTeams(response.data || []))
        .catch(() => setTeams([]));
    }
  }, [isBoss]);

  const handleAnnouncementClick = (announcement) => {
    setSelectedAnnouncementId(announcement._id);
    setDetailDialogOpen(true);
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await announcementService.markAllAsRead();
      setAnnouncements((prev) => prev.map((item) => ({ ...item, isRead: true })));
      window.dispatchEvent(new CustomEvent('announcements-read'));
      toast.success('All announcements marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleDelete = async (e, announcementId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this announcement?')) {
      return;
    }

    try {
      await announcementService.deleteAnnouncement(announcementId);
      toast.success('Announcement deleted');
      fetchAnnouncements();
      window.dispatchEvent(new CustomEvent('announcements-read'));
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleReadUpdate = () => {
    setAnnouncements((prev) =>
      prev.map((item) =>
        item._id === selectedAnnouncementId ? { ...item, isRead: true } : item
      )
    );
    window.dispatchEvent(new CustomEvent('announcements-read'));
  };

  const handleVoteUpdate = ({ upvotes, downvotes }) => {
    setAnnouncements((prev) =>
      prev.map((item) =>
        item._id === selectedAnnouncementId ? { ...item, upvotes, downvotes } : item
      )
    );
  };

  const unreadOnPage = announcements.filter((item) => !item.isRead).length;

  return (
    <Box sx={{ p: 3, maxWidth: 760, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <CampaignIcon color="primary" />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={600} lineHeight={1.2}>
              Announcements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stay up to date with the latest updates
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {unreadOnPage > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllRead}
              disabled={markingAllRead}
            >
              Mark all read
            </Button>
          )}
          {isBoss && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              New Announcement
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((item) => (
            <Skeleton key={item} variant="rounded" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      ) : announcements.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.default'
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <CampaignIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No announcements yet
            </Typography>
            <Typography variant="body2" color="text.disabled">
              New updates will appear here when they are published.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Stack spacing={1.5}>
            {announcements.map((announcement) => (
              <Card
                key={announcement._id}
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeftWidth: announcement.isRead ? '1px' : '4px',
                  borderLeftColor: announcement.isRead ? 'divider' : 'primary.main',
                  bgcolor: 'background.paper',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    boxShadow: 1,
                    borderColor: 'primary.light',
                    borderLeftColor: announcement.isRead ? 'primary.light' : 'primary.main'
                  }
                }}
                onClick={() => handleAnnouncementClick(announcement)}
              >
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                        {!announcement.isRead && (
                          <DotIcon sx={{ fontSize: 10, color: 'primary.main' }} />
                        )}
                        <Typography
                          variant="subtitle1"
                          fontWeight={announcement.isRead ? 500 : 700}
                          sx={{ lineHeight: 1.3 }}
                        >
                          {announcement.title}
                        </Typography>
                        {!announcement.isRead && (
                          <Chip label="Unread" size="small" color="primary" sx={{ height: 22, fontSize: '0.7rem' }} />
                        )}
                        {announcement.attachments?.length > 0 && (
                          <Chip
                            icon={<ImageIcon sx={{ fontSize: '14px !important' }} />}
                            label={announcement.attachments.length}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          mb: 1.5,
                          lineHeight: 1.5
                        }}
                      >
                        {announcement.body}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(announcement.publishedAt)}
                          {isBoss && ` · ${formatAudience(announcement.audience)}`}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <ThumbUpIcon sx={{ fontSize: 15 }} />
                            <Typography variant="caption">{announcement.upvotes || 0}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <ThumbDownIcon sx={{ fontSize: 15 }} />
                            <Typography variant="caption">{announcement.downvotes || 0}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <CommentIcon sx={{ fontSize: 15 }} />
                            <Typography variant="caption">
                              {announcement.commentCount || announcement.comments?.length || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {isBoss && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDelete(e, announcement._id)}
                        title="Delete"
                        sx={{ mt: -0.5, mr: -0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}

      {isBoss && (
        <CreateAnnouncementDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={() => {
            fetchAnnouncements();
            window.dispatchEvent(new CustomEvent('announcements-new'));
          }}
          teams={teams}
        />
      )}

      <AnnouncementDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedAnnouncementId(null);
        }}
        announcementId={selectedAnnouncementId}
        onRead={handleReadUpdate}
        onVoteUpdate={handleVoteUpdate}
      />
    </Box>
  );
};

export default AnnouncementList;
